import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Project, Change, ProjectLabel, User } from '../../models';
import type { ColumnData, BoardFilters } from '../../models';
import { loadProject, createBoard, updateBoard, deleteBoard, RootState, useAppDispatch, useAppSelector } from '../../redux';
import { Filters } from './Filters';
import { Board } from '../../models';
import { Ability, ProjectBoard, Button, Icon, Select, FieldHelp } from '../../components';
import type { IconType } from '../../components';
import BoardHealthModal from '../BoardHealthModal';
import SuggestLabelsModal from '../SuggestLabelsModal';
import { Badge, Button as FButton, TextInput } from 'flowbite-react';
import _ from 'lodash';
import {
    loadProjectViewPrefs,
    saveProjectViewPrefs,
    type ProjectListSort,
} from '../../utils/projectViewPrefs';

interface ProjectBoardsProps {
    changeId?: string;
}

function ProjectBoards(props: ProjectBoardsProps) {
    const { changeId } = props;

    const { t, i18n } = useTranslation();

    const [filters, setFilters]                     = useState<BoardFilters>({} as BoardFilters);
    const [showOptions, setShowOptions]             = useState(false);
    const [activeBoard, setActiveBoard]             = useState<Board>();
    const [currentBoardTitle, setCurrentBoardTitle] = useState("");
    const [newBoardTemplate, setNewBoardTemplate]   = useState<Board>();
    const [newBoardTitle, setNewBoardTitle]         = useState("");
    // null = follow project posture; boolean = user toggle override
    const [listOverride, setListOverride]           = useState<boolean | null>(null);
    const [compactList, setCompactList]             = useState(false);
    const [listSort, setListSort]                   = useState<ProjectListSort>('newest');
    const [prefsProjectId, setPrefsProjectId]       = useState<string | null>(null);
    const [showStatusBreakdown, setShowStatusBreakdown] = useState(false);
    const [showLabelBreakdown, setShowLabelBreakdown] = useState(false);
    const [hideLabelNames, setHideLabelNames] = useState(false);
    const [showBoardHealth, setShowBoardHealth]     = useState(false);
    const [showSuggestLabels, setShowSuggestLabels] = useState(false);

    const { user }  = useAppSelector(state => state.auth);
    const { project } = useAppSelector((state: RootState) => state.projects);
    const { board, updating, deleting, creating } = useAppSelector(state => state.boards);

    const dispatch = useAppDispatch();

    const prefersList = project?.posture?.defaultLanding === 'list';
    const showList = listOverride ?? prefersList;

    React.useEffect(() => {
        if (!project?._id)
            return;

        const prefs = loadProjectViewPrefs(project._id);
        setListOverride(prefs.view === 'list' ? true : prefs.view === 'board' ? false : null);
        setCompactList(prefs.compact);
        setListSort(prefs.sort);
        setFilters({
            search: undefined,
            user: prefs.assigneeId ? { _id: prefs.assigneeId } as User : undefined,
            unassigned: prefs.unassigned,
        } as BoardFilters);
        setPrefsProjectId(project._id);
    }, [project?._id]);

    React.useEffect(() => {
        if (!project?._id || prefsProjectId !== project._id)
            return;

        saveProjectViewPrefs(project._id, {
            view: listOverride === null ? null : (listOverride ? 'list' : 'board'),
            compact: compactList,
            sort: listSort,
            unassigned: !!filters.unassigned,
            assigneeId: filters.user?._id || null,
        });
    }, [
        project?._id,
        prefsProjectId,
        listOverride,
        compactList,
        listSort,
        filters.unassigned,
        filters.user?._id,
    ]);

    // Ignore Redux board from a previous project while the new one loads.
    const liveBoard = board?._id === activeBoard?._id ? board : undefined;

    const filterableUsers = React.useMemo(() => {
        if (!liveBoard)
            return [];

        let users: AnyObj = {};
        liveBoard.columns.forEach((column: ColumnData) => {
            column.changes.forEach((change: Change) => {
                if (change?.assignee?._id)
                    users[change.assignee._id] = change.assignee;
            });
        });

        return Object.values(users);
    }, [liveBoard]);

    React.useEffect(() => {
        const assigneeId = filters.user?._id;
        if (!assigneeId || filters.user?.email)
            return;

        const fullUser = filterableUsers.find((u: User) => u._id === assigneeId);
        if (!fullUser)
            return;

        setFilters((prev) => (
            prev.user?._id === assigneeId ? { ...prev, user: fullUser } : prev
        ));
    }, [filterableUsers, filters.user]);

    const selectBoard = (board: Board) => {
        setActiveBoard(board);
        setCurrentBoardTitle(board.name.fr);
    }

    if (!activeBoard && project?.boards?.length > 0)
        selectBoard(project.boards[0]);

    const handleKeyPress = (e: AnyObj, enterCallback: () => void, escapeCallback?: () => void) => {
        if (e.key === 'Enter' || e.code === 'Enter')
            enterCallback();
        if (escapeCallback && (e.key === 'Escape' || e.code === 'Escape'))
            escapeCallback();
    }

    const renameBoard = () => {
        if (!activeBoard)
            return;

        dispatch(updateBoard({
            _id: activeBoard._id,
            name: {
                'fr': currentBoardTitle,
                'en': currentBoardTitle,
                'de': currentBoardTitle
            }
        }, /*patch*/true));
    };

    const _deleteBoard = () => {
        if (!activeBoard)
            return;

        dispatch(deleteBoard(activeBoard._id, () => {
            dispatch(loadProject(project._id));
            selectBoard(project.boards[0] || null);
        }));
    };

    const addBoard = () => {
        const newBoard = newBoardTemplate ?? {
            project: project._id,
            name: {
                'fr': newBoardTitle,
                'en': newBoardTitle,
                'de': newBoardTitle
            },
            columns: []
        };

        dispatch(createBoard(newBoard, {}, () => {
            setShowOptions(false);
            setNewBoardTemplate(undefined);
            setNewBoardTitle('');
        }));
    };

    const theme = {
        "base": "group flex items-stretch items-center justify-center p-0 text-center font-medium relative outline-none transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow] inline-flex",
        "color": {
            "gray": "text-gray-900 bg-white border border-gray-200 enabled:hover:bg-gray-100 dark:hover:bg-gray-700 enabled:hover:text-cyan-700 focus:text-cyan-700 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 dark:enabled:hover:text-white dark:enabled:hover:bg-gray-700",
            "info": "text-white bg-cyan-700 border border-transparent enabled:hover:bg-cyan-800 dark:hover:bg-cyan-600 dark:enabled:hover:bg-cyan-700 dark:focus"
        }
    };

    const handleFilters = (next: BoardFilters) => {
        setFilters((prev) => ({
            ...prev,
            search: next.search,
            user: next.user,
            unassigned: next.user ? false : prev.unassigned,
        }));
    }

    const toggleUnassignedFilter = () => {
        setFilters((prev) => ({
            ...prev,
            user: undefined,
            unassigned: !prev.unassigned,
        }));
    }

    const toggleStatusFilter = (status: string) => {
        setFilters((prev) => ({
            ...prev,
            status: prev.status === status ? undefined : status,
        }));
    }

    const toggleLabelFilter = React.useCallback((labelId: string) => {
        setFilters((prev) => ({
            ...prev,
            labelId: prev.labelId === labelId ? undefined : labelId,
        }));
        setShowLabelBreakdown(true);
    }, []);

    const boardCanBeRemoved = _.flatten(liveBoard?.columns.map((column: ColumnData) => column.isSmart ? [] : column.changes)).length === 0;

    const predefinedBoards: Record<string, Board> = React.useMemo(() => Board.predefinedBoards(project), [project?._id]);
    const predefinedBoardsOptions = Object.keys(predefinedBoards).map((type: string) => ({
        value: type,
        label: predefinedBoards[type].name[i18n.language + ""],
        leftContent: <Icon type={predefinedBoards[type].icon as IconType} className="ml-2" size={5} />
    }));
    predefinedBoardsOptions.unshift({
        value: "",
        label: t('projects.choose_a_predefined_board'),
        leftContent: <span />
    });

    const selectPredefinedBoard = (type: string | number) => {
        const boards = Board.predefinedBoards(project);
        setNewBoardTemplate(boards[type]);
    }

    const boardContent = React.useMemo(() => {
        if (!activeBoard)
            return null;

        return (
            <ProjectBoard
                boardId={activeBoard._id}
                project={project}
                changeId={changeId}
                filters={filters}
                showList={showList}
                compactList={compactList}
                listSort={listSort}
                onLabelClick={toggleLabelFilter}
                hideLabelNames={hideLabelNames}
            />
        );
    }, [ project?.syncedAt, activeBoard?._id, changeId, showList, compactList, listSort, filters, toggleLabelFilter, hideLabelNames ]);

    const cardCounts = React.useMemo(() => {
        if (!liveBoard)
            return { total: 0, byStatus: {} as Record<string, number>, byLabel: {} as Record<string, number> };

        return liveBoard.cardCounts();
    }, [liveBoard]);

    const statusBreakdown = React.useMemo(() => {
        return Change.allStatus()
            .map((status) => ({
                status,
                count: cardCounts.byStatus[status] || 0,
            }))
            .filter((entry) => entry.count > 0);
    }, [cardCounts]);

    const projectLabels: ProjectLabel[] = project?.labels || [];
    const labelBreakdown = React.useMemo((): { label: ProjectLabel; count: number }[] => {
        return projectLabels
            .map((label: ProjectLabel) => ({
                label,
                count: cardCounts.byLabel?.[label._id] || 0,
            }))
            .sort((a, b) => (
                b.count - a.count || a.label.name.localeCompare(b.label.name)
            ));
    }, [projectLabels, cardCounts]);

    return (
        <div className="ProjectBoards">
            <div className="flex flex-col md:flex-row justify-center space-y-2 md:space-x-2 md:space-y-0">
                <Ability can="board:edit">
                    <Button
                        className="border-none -ml-3"
                        color="none"
                        title=""
                        textColor="cyan-700"
                        iconSize={6}
                        icon={showOptions ? 'close' : 'menu'}
                        onClick={() => setShowOptions(!showOptions)}
                    />
                </Ability>

                <div className="flex-1 flex justify-center items-center md:justify-start gap-2">
                    {board && project.boards.length > 1 && (
                        <FButton.Group>
                            {project.boards.map((board: Board) => (
                                <FButton size="xs" theme={theme} key={board._id} onClick={() => selectBoard(board)} color={board._id === activeBoard?._id ? 'info' : 'gray'}>
                                    <Icon type={board?.icon as IconType} className="inline mr-1.5" color={board._id === activeBoard?._id ? 'white' : 'gray-900'} size={3.5} />
                                    {board.name.fr}
                                </FButton>
                            ))}
                        </FButton.Group>
                    )}
                    {board && project.boards.length === 1 && (
                        <FButton size="xs" theme={theme} key={board?._id} color="info">
                            <Icon type={board?.icon as IconType} className="inline mr-1.5" color={board._id === activeBoard?._id ? 'white' : 'gray-900'} size={3.5} />
                            {board?.name?.fr}
                        </FButton>
                    )}
                    {board && (
                        <FButton
                            size="xs"
                            theme={theme}
                            color={showStatusBreakdown ? 'info' : 'gray'}
                            title={t('projects.board_card_count') as string}
                            aria-label={t('projects.board_card_count') as string}
                            aria-expanded={showStatusBreakdown}
                            onClick={() => setShowStatusBreakdown(!showStatusBreakdown)}
                        >
                            <Icon
                                type="list"
                                className="inline mr-1.5"
                                color={showStatusBreakdown ? 'white' : 'gray-900'}
                                size={3.5}
                            />
                            {cardCounts.total}
                        </FButton>
                    )}
                    {board && (
                        <FButton
                            size="xs"
                            theme={theme}
                            color={showLabelBreakdown ? 'info' : 'gray'}
                            title={t('projects.board_label_filters') as string}
                            aria-label={t('projects.board_label_filters') as string}
                            aria-expanded={showLabelBreakdown}
                            onClick={() => setShowLabelBreakdown(!showLabelBreakdown)}
                        >
                            <Icon
                                type="tag"
                                className="inline mr-1.5"
                                color={showLabelBreakdown ? 'white' : 'gray-900'}
                                size={3.5}
                            />
                            {projectLabels.length}
                        </FButton>
                    )}
                    {board && (
                        <FButton
                            size="xs"
                            theme={theme}
                            color={showBoardHealth ? 'info' : 'gray'}
                            title={t('projects.board_health_title') as string}
                            aria-label={t('projects.board_health_title') as string}
                            onClick={() => setShowBoardHealth(true)}
                        >
                            <Icon
                                type="chart"
                                className="inline mr-1.5"
                                color={showBoardHealth ? 'white' : 'gray-900'}
                                size={3.5}
                            />
                            {t('projects.board_health')}
                        </FButton>
                    )}
                </div>
                <div className="flex justify-center items-center md:justify-start gap-1">
                    {(() => {
                        const listFirst = project?.posture?.defaultLanding === 'list';
                        const boardBtn = (
                            <FButton key="board" size="xs" theme={theme} onClick={() => setListOverride(false)} color={!showList ? 'info' : 'gray'}>
                                <Icon type="board" className="inline mr-1.5" color={!showList ? 'white' : 'gray-900'} size={3.5} />
                                {t('projects.kanban_view')}
                            </FButton>
                        );
                        const listBtn = (
                            <FButton key="list" size="xs" theme={theme} onClick={() => setListOverride(true)} color={showList ? 'info' : 'gray'}>
                                <Icon type="list" className="inline mr-1.5" color={showList ? 'white' : 'gray-900'} size={3.5} />
                                {t('projects.list_view')}
                            </FButton>
                        );
                        return (
                            <FButton.Group>
                                {listFirst ? listBtn : boardBtn}
                                {listFirst ? boardBtn : listBtn}
                            </FButton.Group>
                        );
                    })()}
                    {showList && (
                        <>
                            <FButton
                                size="xs"
                                theme={theme}
                                color={compactList ? 'info' : 'gray'}
                                onClick={() => setCompactList((v) => !v)}
                                title={t('projects.compact_list') as string}
                            >
                                <Icon type="list" className="inline" color={compactList ? 'white' : 'gray-900'} size={3.5} />
                            </FButton>
                            <select
                                className="rounded-md border-gray-300 dark:border-gray-600 text-xs py-1"
                                value={listSort}
                                onChange={(e) => setListSort(e.target.value as ProjectListSort)}
                                title={t('projects.list_sort') as string}
                                aria-label={t('projects.list_sort') as string}
                            >
                                <option value="newest">{t('projects.list_sort_newest')}</option>
                                <option value="oldest">{t('projects.list_sort_oldest')}</option>
                                <option value="priority">{t('projects.list_sort_priority')}</option>
                            </select>
                        </>
                    )}
                </div>
                <div className="flex-1 flex justify-center md:justify-end items-center gap-1">
                    <FieldHelp content={t('projects.help.board_filters') || ''} />
                    <FButton
                        size="xs"
                        theme={theme}
                        color={filters.unassigned ? 'info' : 'gray'}
                        onClick={toggleUnassignedFilter}
                        title={t('projects.filter_unassigned') as string}
                    >
                        <Icon type="user" className="inline mr-1" color={filters.unassigned ? 'white' : 'gray-900'} size={3.5} />
                        {t('projects.filter_unassigned')}
                    </FButton>
                    <Filters
                        key={prefsProjectId || 'idle'}
                        currentUser={user}
                        users={filterableUsers}
                        onChange={handleFilters}
                        initialValues={{ search: '', user: filters.user }}
                    />
                </div>
            </div>

            {board && showStatusBreakdown && (
                <div
                    className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
                    aria-label={t('projects.board_status_breakdown') as string}
                >
                    {statusBreakdown.length === 0 ? (
                        <span>0</span>
                    ) : (
                        statusBreakdown.map(({ status, count }) => {
                            const active = filters.status === status;
                            return (
                                <Badge
                                    key={status}
                                    color={active ? 'info' : 'light'}
                                    className="w-fit cursor-pointer whitespace-nowrap font-normal"
                                    onClick={() => toggleStatusFilter(status)}
                                >
                                    <span className="font-normal">{t(`changes.progress_status_type.${status}`)}</span>
                                    <span className="ml-1 font-semibold">{count}</span>
                                </Badge>
                            );
                        })
                    )}
                </div>
            )}

            {showLabelBreakdown && (
                <div
                    className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
                    aria-label={t('projects.board_label_filters') as string}
                >
                    <Ability can="project:edit">
                        <button
                            type="button"
                            className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${
                                showSuggestLabels
                                    ? 'border-cyan-600 bg-cyan-50 dark:bg-cyan-950/30 text-cyan-800'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                            onClick={() => setShowSuggestLabels(true)}
                            title={t('projects.suggest_labels_title') || ''}
                        >
                            <Icon type="magic" size={3} color="inherit" />
                            {t('projects.suggest_labels')}
                        </button>
                    </Ability>
                    {labelBreakdown.length === 0 ? (
                        <span className="text-gray-500 dark:text-gray-400 italic">
                            {t('projects.board_labels_empty')}
                        </span>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${
                                    hideLabelNames
                                        ? 'border-cyan-600 bg-cyan-50 dark:bg-cyan-950/30 text-cyan-800'
                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                                onClick={() => setHideLabelNames(!hideLabelNames)}
                                title={t(hideLabelNames ? 'projects.show_label_names' : 'projects.hide_label_names') || ''}
                            >
                                <Icon type="eye" size={3} color="inherit" />
                                {t('projects.hide_label_names')}
                            </button>
                            {labelBreakdown.map(({ label, count }) => {
                                const active = filters.labelId === label._id;
                                return (
                                    <button
                                        key={label._id}
                                        type="button"
                                        className={`inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-xs font-medium border ${active ? 'ring-2 ring-offset-1 ring-cyan-600' : 'border-transparent opacity-90 hover:opacity-100'}`}
                                        style={{
                                            backgroundColor: label.color,
                                            color: ProjectLabel.textColor(label.color),
                                        }}
                                        onClick={() => toggleLabelFilter(label._id)}
                                        title={label.name}
                                    >
                                        <Icon type="tag" size={3} className="shrink-0" color="inherit" />
                                        {label.name}
                                        <span className="opacity-90">({count})</span>
                                    </button>
                                );
                            })}
                        </>
                    )}
                </div>
            )}

            <div className="max-w-[calc(100vw-2rem)] mt-1 overflow-x-clip">
                <div className="flex flex-row justify-stretch items-stretch">
                    {showOptions && (
                        <div className={`relative pr-2 ${showOptions ? 'flex flex-col space-y-3' : 'hidden'} min-w-[280px]`}>
                            {board && (
                                <Ability can="board:edit">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-800 h-100">
                                        <h3 className="font-medium text-sm">
                                            {t('projects.rename_board')}
                                        </h3>
                                        <TextInput
                                            sizing="sm"
                                            rightIcon={() => <Icon type={updating === 'pending' ? 'loading' : 'edit'} />}
                                            placeholder={t('projects.rename_board') as string}
                                            type="text"
                                            onChange={e => setCurrentBoardTitle(e.target.value)}
                                            onKeyUp={(e) => handleKeyPress(e, renameBoard)}
                                            value={currentBoardTitle}
                                        />
                                    </div>
                                    {board && boardCanBeRemoved && (
                                    <div className="p-2 bg-gray-100 dark:bg-gray-800 h-100">
                                        <Ability can="board:delete">
                                            <h3 className="font-medium text-sm">
                                                {t('projects.delete_this_board')}
                                            </h3>
                                            <div className="flex justify-center">
                                                <Button
                                                    small
                                                    className="mt-2"
                                                    color="danger"
                                                    title={t('projects.delete_this_board')}
                                                    iconSize={4}
                                                    icon={deleting === 'pending' ? 'loading' : 'delete'}
                                                    onClick={_deleteBoard}
                                                />
                                            </div>
                                        </Ability>
                                    </div>
                                    )}
                                </Ability>
                            )}

                            <Ability can="board:create">
                                <div className="p-2 flex flex-col space-y-2 bg-gray-100 dark:bg-gray-800 h-100">
                                    <h3 className="font-medium text-sm inline-flex items-center gap-1">
                                        {t('projects.add_board')}
                                        <FieldHelp content={t('projects.help.board_template') || ''} />
                                    </h3>
                                    <Select
                                        options={predefinedBoardsOptions}
                                        onChange={selectPredefinedBoard}
                                    />
                                    {!newBoardTemplate && (
                                        <>
                                            <div className=" flex flex-row space-x-1 justify-center items-center">
                                                <hr className="flex-1 mb-0"/>
                                                <span className="text-xs font-bold">
                                                    {t('common.or')}
                                                </span>
                                                <hr className="flex-1 mb-0"/>
                                            </div>
                                            <TextInput
                                                sizing="sm"
                                                rightIcon={() => <Icon type="plus" />}
                                                placeholder={t('projects.add_empty_board') as string}
                                                type="text"
                                                onChange={e => setNewBoardTitle(e.target.value)}
                                                onKeyUp={(e) => handleKeyPress(e, addBoard, () => setNewBoardTitle(""))}
                                                value={newBoardTitle}
                                            />
                                        </>
                                    )}
                                    {(newBoardTemplate || newBoardTitle) && (
                                        <div className="flex justify-center">
                                            <Button
                                                small
                                                className="mt-2"
                                                color="primary"
                                                title={t('common.add')}
                                                icon={creating === 'pending' ? 'loading' : 'plus'}
                                                iconSize={4}
                                                onClick={() => addBoard()}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Ability>
                        </div>
                    )}
                    { boardContent }
                </div>
            </div>

            <BoardHealthModal
                show={showBoardHealth}
                board={liveBoard}
                project={project}
                onClose={() => setShowBoardHealth(false)}
            />
            <SuggestLabelsModal
                show={showSuggestLabels}
                boardId={liveBoard?._id || activeBoard?._id}
                project={project}
                onClose={() => setShowSuggestLabels(false)}
            />
        </div>
    );
}

export default ProjectBoards;
